import { useState } from "react";
import Box from "@mui/material/Box";
import {
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";

import useSolutionList from "../hooks/useSolutionList";

import SolutionCard from "./SolutionCard";
import SolutionTable from "./SolutionTable";
import FullPageLoader from "../../common/FullPageLoader";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import InboxIcon from "@mui/icons-material/Inbox";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";
import routes from "../../../routes";

const style = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
} as const;

function SolutionList() {
  const { solutionList, error, loading } = useSolutionList();
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const [viewMode, setViewMode] = useState<"card" | "table">("card");

  if (loading) return <FullPageLoader loading={loading} />;
  if (error) showBoundary(error);

  const handleViewModeChange = (_: unknown, newViewMode: "card" | "table") => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <Box sx={style.pageContainer}>
      {solutionList && solutionList.length > 0 ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view-mode"
            >
              <ToggleButton
                value="card"
                aria-label="card-view"
                data-testid="card-view-button"
              >
                <Tooltip title="Card View">
                  <ViewModuleIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="table"
                aria-label="table-view"
                data-testid="table-view-button"
              >
                <Tooltip title="Table View">
                  <TableRowsIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              data-testid="add-solution-button-header"
              sx={{ alignSelf: "end", ml: 2 }}
              variant="contained"
              onClick={() => {
                navigate(routes.solutions.new.pattern);
              }}
            >
              Add Project
            </Button>
          </Box>
          {viewMode === "card" ? (
            <Box
              data-testid="solution-list"
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              {solutionList.map((solution) => (
                <SolutionCard
                  key={solution.id}
                  id={solution.id}
                  name={solution.name}
                  developer={solution.developer}
                  description={solution.shortDescription}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ width: "calc(100%)", height: "100%" }}>
              <SolutionTable
                solutionList={solutionList}
                loading={loading}
                data-testid="solution-list-table"
              />
            </Box>
          )}
        </>
      ) : (
        <Box>
          <InboxIcon data-testid="no-solutions-icon" fontSize="large" />
          <Typography
            variant="h5"
            sx={{ paddingBottom: 2 }}
            data-testid="no-solution-list"
          >
            Uh, oh...No projects to display!
          </Typography>
          <Button
            data-testid="add-solution-button-empty"
            sx={{ alignSelf: "center", ml: 2 }}
            variant="contained"
            onClick={() => navigate(routes.solutions.new.pattern)}
          >
            Add Project
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default SolutionList;
